/**
 * TELA 9 — CONFIGURAÇÕES
 *
 * Quatro blocos: empresa, Evolution, política de descanso da base e alertas.
 * O seletor de tema **não** está aqui — ele vive na casca, e duplicar um
 * controle é o jeito mais rápido de ninguém saber qual dos dois vale.
 *
 * A REGRA QUE MANDA NESTA TELA: **a API key nunca aparece.** Nem inteira, nem
 * mascarada, nem em `title`, nem em `value` de input. O contrato entrega
 * `apiKeyDefinida: boolean` — existe ou não existe — e é só isso que a tela
 * mostra. Segredo em texto plano espalhado por projeto é uma cicatriz já paga
 * nesta casa; máscara de asteriscos ainda é o segredo trafegando até o
 * navegador.
 *
 * O campo de troca é de escrita apenas: digitar substitui, vazio não mexe.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type Configuracoes } from "../api.js";
import {
  Aviso,
  Botao,
  Campo,
  Painel,
  Selo,
  classeEntrada,
} from "../ui/base.js";
import {
  Conteudo,
  Grade,
  Leitura,
  Marcar,
  fmtDataHora,
  useCarga,
} from "../ui/comercial.js";

/** Os fusos que a operação usa. O fuso manda na janela de envio dos planos. */
const FUSOS = [
  "America/Sao_Paulo",
  "America/Manaus",
  "America/Belem",
  "America/Cuiaba",
  "America/Rio_Branco",
] as const;

const nomeDoFuso = (f: string): string =>
  f.replace("America/", "").replace(/_/g, " ");

export function Config(): JSX.Element {
  const buscar = useCallback(() => api.config(), []);
  const { estado, recarregar } = useCarga(buscar);

  return (
    <Conteudo estado={estado} aoRecarregar={recarregar}>
      {(c) => <CorpoDaConfig inicial={c} aoRecarregar={recarregar} />}
    </Conteudo>
  );
}

/** Estado de gravação de um bloco. Cada bloco salva sozinho. */
type Gravacao =
  | { fase: "parado" }
  | { fase: "salvando" }
  | { fase: "ok" }
  | { fase: "erro"; mensagem: string };

function useGravar(
  aoRecarregar: () => void,
): [Gravacao, (corpo: unknown) => Promise<void>] {
  const [g, setG] = useState<Gravacao>({ fase: "parado" });
  const salvar = async (corpo: unknown): Promise<void> => {
    setG({ fase: "salvando" });
    try {
      await api.salvarConfig(corpo);
      setG({ fase: "ok" });
      aoRecarregar();
    } catch (e: unknown) {
      setG({
        fase: "erro",
        mensagem:
          e instanceof Error ? e.message : "O servidor recusou a alteração.",
      });
    }
  };
  return [g, salvar];
}

function Estado({ g }: { g: Gravacao }): JSX.Element | null {
  if (g.fase === "ok")
    return <span className="text-[12px] text-ok">Salvo.</span>;
  if (g.fase === "erro")
    return (
      <span role="alert" className="text-[12px] text-crit max-w-[40ch]">
        {g.mensagem}
      </span>
    );
  return null;
}

function CorpoDaConfig({
  inicial,
  aoRecarregar,
}: {
  inicial: Configuracoes;
  aoRecarregar: () => void;
}): JSX.Element {
  return (
    <div className="grid gap-4">
      <BlocoEmpresa inicial={inicial} aoRecarregar={aoRecarregar} />
      <BlocoEvolution inicial={inicial} aoRecarregar={aoRecarregar} />
      <BlocoDescanso inicial={inicial} aoRecarregar={aoRecarregar} />
      <BlocoAlertas inicial={inicial} aoRecarregar={aoRecarregar} />

      <Painel titulo="Aparência" dica="Onde fica o controle de tema">
        <p className="text-[13px] text-tinta-2 leading-relaxed max-w-[80ch]">
          O tema é escolhido no rodapé do menu lateral, e vale para este
          navegador. Ele muda a base e o destaque —{" "}
          <strong>nunca a leitura da operação</strong>: atividade, apto,
          atenção, risco e parado continuam com a mesma cor nos três temas. Em{" "}
          <strong>Automático</strong>, a página acompanha o tema do sistema e
          vira junto, sem recarregar.
        </p>
      </Painel>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Empresa
   ══════════════════════════════════════════════════════════════════════ */

function BlocoEmpresa({
  inicial,
  aoRecarregar,
}: {
  inicial: Configuracoes;
  aoRecarregar: () => void;
}): JSX.Element {
  const [nome, setNome] = useState(inicial.empresa.nome);
  const [documento, setDocumento] = useState(inicial.empresa.documento ?? "");
  const [fuso, setFuso] = useState(inicial.empresa.fuso);
  const [g, salvar] = useGravar(aoRecarregar);

  // O fuso do servidor pode não estar na lista curta; ele entra como opção
  // extra em vez de ser silenciosamente trocado por outro.
  const fusos = FUSOS.includes(fuso as (typeof FUSOS)[number])
    ? [...FUSOS]
    : [fuso, ...FUSOS];

  return (
    <Painel
      titulo="Empresa"
      dica="Contexto institucional da operação"
      acoes={
        <>
          <Estado g={g} />
          <Botao
            pequeno
            tipo="principal"
            desabilitado={g.fase === "salvando" || nome.trim().length < 2}
            onClick={() =>
              void salvar({
                empresa: {
                  nome: nome.trim(),
                  documento: documento.trim() === "" ? null : documento.trim(),
                  fuso,
                },
              })
            }
          >
            {g.fase === "salvando" ? "Salvando…" : "Salvar"}
          </Botao>
        </>
      }
    >
      <Grade>
        <Campo rotulo="Razão social">
          <input
            className={classeEntrada}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
        </Campo>
        <Campo rotulo="CNPJ" dica="De quem é a operação — e, por consequência, de quem são os chips">
          <input
            className={classeEntrada}
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
            placeholder="00.000.000/0000-00"
          />
        </Campo>
        <Campo
          rotulo="Fuso horário"
          dica="Vale para a janela dos planos e para o relógio do motor"
        >
          <select
            className={classeEntrada}
            value={fuso}
            onChange={(e) => setFuso(e.target.value)}
          >
            {fusos.map((f) => (
              <option key={f} value={f}>
                {nomeDoFuso(f)}
              </option>
            ))}
          </select>
        </Campo>
      </Grade>
    </Painel>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Evolution
   ══════════════════════════════════════════════════════════════════════ */

function BlocoEvolution({
  inicial,
  aoRecarregar,
}: {
  inicial: Configuracoes;
  aoRecarregar: () => void;
}): JSX.Element {
  const ev = inicial.evolution;
  const [url, setUrl] = useState(ev.url);
  const [webhook, setWebhook] = useState(ev.webhook ?? "");
  /**
   * Escrita apenas. Nunca é preenchido a partir do servidor — o servidor não
   * manda a chave, e a tela não guarda o que digitou depois de enviar.
   */
  const [novaChave, setNovaChave] = useState("");
  const [g, salvar] = useGravar(aoRecarregar);

  // Depois de gravar, apaga a chave da memória do componente.
  useEffect(() => {
    if (g.fase === "ok") setNovaChave("");
  }, [g.fase]);

  return (
    <Painel
      titulo="Evolution API"
      dica={
        ev.apiKeyDefinida
          ? `${ev.instanciasMapeadas} instância(s) mapeada(s)`
          : "Sem chave definida — a integração está parada"
      }
      acoes={
        <>
          <Estado g={g} />
          <Botao
            pequeno
            tipo="principal"
            desabilitado={g.fase === "salvando"}
            onClick={() =>
              void salvar({
                evolution: {
                  url: url.trim(),
                  webhook: webhook.trim() === "" ? null : webhook.trim(),
                  // Chave vazia significa "não mexer", nunca "apagar".
                  ...(novaChave.trim() !== ""
                    ? { apiKey: novaChave.trim() }
                    : {}),
                },
              })
            }
          >
            {g.fase === "salvando" ? "Salvando…" : "Salvar"}
          </Botao>
        </>
      }
    >
      <Grade>
        <Campo rotulo="Servidor" dica="O mesmo servidor aquece os números e recebe as respostas">
          <input
            className={classeEntrada}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
          />
        </Campo>
        <Campo rotulo="Webhook" dica="Para onde o Evolution entrega os eventos">
          <input
            className={classeEntrada}
            value={webhook}
            onChange={(e) => setWebhook(e.target.value)}
            placeholder="https://…/webhook/evolution"
          />
        </Campo>
        <Campo
          rotulo="Substituir a API key"
          dica="Campo de escrita apenas. Em branco, a chave atual permanece intocada."
        >
          <input
            type="password"
            autoComplete="off"
            className={classeEntrada}
            value={novaChave}
            onChange={(e) => setNovaChave(e.target.value)}
            placeholder="deixe em branco para não alterar"
          />
        </Campo>
      </Grade>

      <div className="mt-4">
        <Grade>
          <Leitura rotulo="API key">
            {/* Só a existência. Nunca o valor, nem mascarado. */}
            {ev.apiKeyDefinida ? (
              <Selo tom="ok">definida</Selo>
            ) : (
              <Selo tom="crit">não informada</Selo>
            )}
          </Leitura>
          <Leitura rotulo="Instâncias mapeadas">
            {ev.instanciasMapeadas}
          </Leitura>
          <Leitura rotulo="Última sincronização">
            {fmtDataHora(ev.ultimaSync)}
          </Leitura>
        </Grade>
      </div>

      <div className="mt-4">
        <Aviso tom="cool">
          <strong>A chave nunca é exibida, nem mascarada.</strong> O servidor
          informa apenas se ela existe. Máscara de asteriscos parece cuidado,
          mas exige mandar o segredo até o navegador — e é assim que ele acaba
          em print, em log e em relatório.
        </Aviso>
      </div>

      {!ev.apiKeyDefinida && (
        <div className="mt-3">
          <Aviso tom="warn">
            Sem chave, nada sai e nada entra: o aquecimento não executa
            atividade e as respostas dos leads não chegam. Defina a chave acima
            antes de aprovar qualquer campanha.
          </Aviso>
        </div>
      )}
    </Painel>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Política de descanso da base
   ══════════════════════════════════════════════════════════════════════ */

function BlocoDescanso({
  inicial,
  aoRecarregar,
}: {
  inicial: Configuracoes;
  aoRecarregar: () => void;
}): JSX.Element {
  const [d, setD] = useState(inicial.descanso);
  const [g, salvar] = useGravar(aoRecarregar);

  const campo = (
    chave: keyof Omit<Configuracoes["descanso"], "contaDiscadora" | "definida">,
    rotulo: string,
    dica: string,
  ): JSX.Element => (
    <Campo rotulo={rotulo} dica={dica}>
      <input
        type="number"
        min={0}
        className={classeEntrada}
        value={d[chave]}
        onChange={(e) => {
          const n = Number(e.target.value);
          setD({ ...d, [chave]: Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 0 });
        }}
      />
    </Campo>
  );

  return (
    <Painel
      titulo="Descanso da base"
      dica="Quanto tempo um contato fica em paz depois de ser tocado"
      acoes={
        <>
          <Estado g={g} />
          <Botao
            pequeno
            tipo="principal"
            desabilitado={g.fase === "salvando"}
            onClick={() => void salvar({ descanso: d })}
          >
            {g.fase === "salvando" ? "Salvando…" : "Salvar"}
          </Botao>
        </>
      }
    >
      {!d.definida && (
        <Aviso tom="crit">
          <strong>Esta política ainda não foi salva.</strong> Os valores abaixo
          são sugestão, não estão valendo — e enquanto for assim a conferência
          da base <strong>suprime todos os contatos</strong> de toda campanha,
          com o motivo “empresa sem política de descanso”. A importação parece
          funcionar e nada é enviado. Confira os números e clique em Salvar.
        </Aviso>
      )}

      <Grade>
        {campo(
          "diasQualquerChip",
          "Dias por qualquer chip",
          "Piso geral: depois de falar com o contato, ninguém volta antes disso — venha de qual número vier",
        )}
        {campo(
          "diasMesmoChip",
          "Dias pelo mesmo chip",
          "Repetição do mesmo número para a mesma pessoa é o padrão que mais parece máquina",
        )}
        {campo(
          "diasMesmoProduto",
          "Dias pelo mesmo produto",
          "Insistir na mesma oferta é o que o contato lê como perseguição, não como follow-up",
        )}
        {campo(
          "maxToques90d",
          "Teto de toques em 90 dias",
          "Limite duro por contato na janela curta",
        )}
        {campo(
          "maxToquesAno",
          "Teto de toques no ano",
          "Limite duro por contato na janela longa",
        )}
      </Grade>

      <div className="mt-4">
        <Marcar
          marcado={d.contaDiscadora}
          aoMudar={(v) => setD({ ...d, contaDiscadora: v })}
          rotulo="A discadora conta como toque"
          dica="Se a operação também liga para o contato, a ligação entra na mesma conta. Ignorar o telefone é como medir só metade do incômodo e concluir que ele é pequeno."
        />
      </div>

      <div className="mt-4">
        <Aviso tom="warn">
          <strong>Esta política é supressão, não sugestão.</strong> Ela é
          aplicada no recorte da campanha — contato em descanso sai da base
          antes do disparo, e o motivo aparece no relatório de supressão. Afrouxar
          estes números aumenta o volume disponível na mesma medida em que
          aumenta a chance de denúncia, que é o sinal que derruba número.
        </Aviso>
      </div>
    </Painel>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Alertas
   ══════════════════════════════════════════════════════════════════════ */

function BlocoAlertas({
  inicial,
  aoRecarregar,
}: {
  inicial: Configuracoes;
  aoRecarregar: () => void;
}): JSX.Element {
  const [a, setA] = useState(inicial.alertas);
  const [g, salvar] = useGravar(aoRecarregar);

  return (
    <Painel
      titulo="Alertas"
      dica="Quando a plataforma deve chamar você"
      acoes={
        <>
          <Estado g={g} />
          <Botao
            pequeno
            tipo="principal"
            desabilitado={g.fase === "salvando"}
            onClick={() => void salvar({ alertas: a })}
          >
            {g.fase === "salvando" ? "Salvando…" : "Salvar"}
          </Botao>
        </>
      }
    >
      <Grade colunas={2}>
        <Campo
          rotulo="Avisar quando a saúde cair abaixo de"
          dica="Em pontos percentuais"
        >
          <input
            type="number"
            min={0}
            max={100}
            className={classeEntrada}
            value={a.saudeAbaixo}
            onChange={(e) => {
              const n = Number(e.target.value);
              setA({
                ...a,
                saudeAbaixo: Number.isFinite(n)
                  ? Math.min(100, Math.max(0, Math.trunc(n)))
                  : 0,
              });
            }}
          />
        </Campo>
        <Campo
          rotulo="Avisar a partir de quantas quedas no dia"
          dica="Queda correlacionada é sinal de vetor comum, não de azar"
        >
          <input
            type="number"
            min={1}
            max={24}
            className={classeEntrada}
            value={a.quedasNoDia}
            onChange={(e) => {
              const n = Number(e.target.value);
              setA({
                ...a,
                quedasNoDia: Number.isFinite(n)
                  ? Math.min(24, Math.max(1, Math.trunc(n)))
                  : 1,
              });
            }}
          />
        </Campo>
      </Grade>

      <div className="mt-4">
        <Marcar
          marcado={a.porEmail}
          aoMudar={(v) => setA({ ...a, porEmail: v })}
          rotulo="Enviar resumo diário por e-mail"
          dica="Os limites acima alimentam o bloco “Precisam de você” do painel, independentemente do e-mail."
        />
      </div>
    </Painel>
  );
}
